<script setup lang="ts">
import { computed } from 'vue'

import BetaEditorViewport from '@/components/beta-editor/canvas/organisms/BetaEditorViewport.vue'
import BetaEditorSidebar from '@/components/beta-editor/BetaEditorSidebar.vue'
import BetaEditorSitePreview from '@/components/beta-editor/BetaEditorSitePreview.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'

const props = withDefaults(defineProps<{
    id: string
    withSidebar?: boolean
    sitePreviewUrl?: string | null
    sitePreviewUnavailableReason?: string | null
}>(), {
    withSidebar: true,
    sitePreviewUrl: null,
    sitePreviewUnavailableReason: null,
})

const emit = defineEmits(['background-click'])

const betaStore = useEditorBetaStore()

const previewDevice = computed(() => betaStore.previewDevice)
</script>

<template>
    <div :id="props.id" class="editor-beta-shell" :data-preview-device="previewDevice">
        <slot name="menu" />

        <div :class="['editor-beta-shell__content', betaStore.isSitePreviewActive && 'is-site-preview-active']">
            <div class="editor-beta-shell__workspace relative z-10">
                <div
                    class="editor-beta-shell__body"
                    :data-preview-device="previewDevice"
                    @pointerdown.self="emit('background-click')"
                >
                    <beta-editor-viewport :device="previewDevice">
                        <beta-editor-site-preview
                            v-if="betaStore.isSitePreviewActive"
                            :src="props.sitePreviewUrl"
                            :device="previewDevice"
                            :unavailable-reason="props.sitePreviewUnavailableReason"
                        />
                        <slot v-else />
                    </beta-editor-viewport>
                </div>
            </div>

            <div v-if="!betaStore.isSitePreviewActive && props.withSidebar" class="editor-beta-shell__sidebar">
                <beta-editor-sidebar />
            </div>
        </div>
    </div>
</template>

<style scoped>
.editor-beta-shell {
    @apply min-h-screen bg-[#F3F5FA];
}

.editor-beta-shell__content {
    @apply grid min-h-screen grid-cols-[minmax(0,1fr)_20rem] pt-[76px] xl:grid-cols-[minmax(0,1fr)_22rem];
}

.editor-beta-shell__content.is-site-preview-active {
    @apply grid-cols-1;
}

.editor-beta-shell__workspace {
    @apply min-w-0;
}

.editor-beta-shell__body {
    @apply relative flex min-h-[calc(100vh-76px)] justify-center px-4 pb-16 pt-8 md:px-6 lg:px-10;
}

.editor-beta-shell__sidebar {
    @apply sticky top-[76px] h-[calc(100vh-76px)] min-w-0 border-l border-[#E3EAF4] bg-[#FAFAFA];
}

@media (max-width: 1024px) {
    .editor-beta-shell__content {
        @apply grid-cols-1;
    }

    .editor-beta-shell__sidebar {
        @apply static h-auto;
    }
}
</style>
